import React, { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { checkMarketplaceDomainApi, createMarketplaceAppApi } from '../../api/marketplaceApps.api';
import { READY_APP_DOMAIN_SUFFIX } from '../../config/readyApps';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { refreshMyPackage } from '../../hooks/useMyPackage';

const SUBDOMAIN_REGEX = /^(?=.{3,63}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const DOMAIN_CHECK_DEBOUNCE_MS = 500;

const normalizeSubdomain = (value, domainSuffix = READY_APP_DOMAIN_SUFFIX) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(new RegExp(`\\.${String(domainSuffix).replace(/\./g, '\\.')}$`, 'i'), '')
    .replace(/\/+$/, '')
    .split('.')[0];

export const DeployReadyAppModal = ({ open, app, onClose, onCreated }) => {
  const { t, language } = useI18n();
  const [title, setTitle] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [fqdn, setFqdn] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [cpu, setCpu] = useState(1);
  const [memoryMb, setMemoryMb] = useState(1024);
  const suffix = READY_APP_DOMAIN_SUFFIX;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setTitle(language === 'fa' ? app?.titleFa || '' : app?.titleEn || '');
    setSubdomain('');
    setCpu(Number(app?.defaultCpu || 1));
    setMemoryMb(Number(app?.defaultMemoryMb || 1024));
    setChecking(false);
    setAvailable(null);
    setFqdn('');
    setError('');
    setCreating(false);
    return undefined;
  }, [open, app?.key, app?.titleFa, app?.titleEn, app?.defaultCpu, app?.defaultMemoryMb, language]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const normalized = normalizeSubdomain(subdomain, suffix);
    if (!normalized) {
      setAvailable(null);
      setFqdn('');
      setError('');
      return undefined;
    }

    if (!SUBDOMAIN_REGEX.test(normalized)) {
      setAvailable(false);
      setFqdn(`${normalized}.${suffix}`);
      setError(t('readyAppsSubdomainInvalid'));
      return undefined;
    }

    setChecking(true);
    setError('');
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkMarketplaceDomainApi(normalized);
        setFqdn(result.fqdn);
        setAvailable(result.available);
        setError(result.available ? '' : t('readyAppsDomainUnavailable'));
      } catch (checkError) {
        setAvailable(false);
        setError(checkError?.message || t('readyAppsDomainCheckError'));
      } finally {
        setChecking(false);
      }
    }, DOMAIN_CHECK_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [open, subdomain, suffix, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!app?.key || creating || available !== true || !title.trim()) {
      return;
    }

    setCreating(true);
    setError('');
    try {
      const created = await createMarketplaceAppApi({
        catalogKey: app.key,
        subdomain: normalizeSubdomain(subdomain, suffix),
        title: title.trim(),
        cpuLimit: cpu,
        memoryMb
      });
      await refreshMyPackage();
      onCreated?.(created);
      onClose?.();
    } catch (createError) {
      setError(createError?.message || t('readyAppsCreateError'));
    } finally {
      setCreating(false);
    }
  };

  const catalogTitle = language === 'fa' ? app?.titleFa : app?.titleEn;

  return (
    <Modal open={open} onClose={onClose} closeDisabled={creating} title={t('readyAppsDeployTitle', { name: catalogTitle || '' })}>
      <form className="space-y-4 px-2 pb-3" onSubmit={handleSubmit}>
        <p className="text-sm leading-6 text-neutral-500">{t('readyAppsDeployHint', { suffix })}</p>
        <label className="block space-y-2">
          <span className="text-sm font-medium text-neutral-500">{t('readyAppsTitleLabel')}</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoComplete="off"
            maxLength={80}
            placeholder={t('readyAppsTitlePlaceholder')}
            disabled={creating}
            className="w-full rounded-lg border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-600"
          />
        </label>
        <label className="block space-y-2">
          <span className="text-sm font-medium text-neutral-500">{t('readyAppsSubdomainLabel')}</span>
          <div className="flex overflow-hidden rounded-lg border border-neutral-200 bg-white focus-within:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-950 dark:focus-within:border-neutral-600">
            <input
              value={subdomain}
              onChange={(event) => setSubdomain(event.target.value)}
              autoComplete="off"
              placeholder="board"
              disabled={creating}
              className="min-w-0 flex-1 border-0 bg-transparent px-3.5 py-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-neutral-100"
            />
            <span dir="ltr" className="hidden shrink-0 items-center border-s border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500 sm:inline-flex dark:border-neutral-800 dark:bg-neutral-900">
              .{suffix}
            </span>
          </div>
        </label>
        <div
          dir="ltr"
          className="rounded-xl bg-neutral-50 px-3.5 py-3 font-mono text-sm text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200"
        >
          {app?.kind === 'Database' ? (fqdn || `—.${suffix}`) : `http://${fqdn || `—.${suffix}`}`}
        </div>
        <label className="block space-y-2">
          <span className="flex justify-between text-sm font-medium text-neutral-500">
            <span>{t('readyAppsCpuLabel')}</span>
            <span dir="ltr">{t('readyAppsCpuHint', { value: cpu })}</span>
          </span>
          <input
            type="range"
            min={app?.minCpu || 0.25}
            max={app?.maxCpu || 8}
            step="0.25"
            value={cpu}
            disabled={creating}
            onChange={(event) => setCpu(Number(event.target.value))}
            className="w-full"
          />
        </label>
        <label className="block space-y-2">
          <span className="flex justify-between text-sm font-medium text-neutral-500">
            <span>{t('readyAppsRamLabel')}</span>
            <span dir="ltr">{t('readyAppsRamHint', { value: memoryMb })}</span>
          </span>
          <input
            type="range"
            min={app?.minMemoryMb || 256}
            max={app?.maxMemoryMb || 8192}
            step="256"
            value={memoryMb}
            disabled={creating}
            onChange={(event) => setMemoryMb(Number(event.target.value))}
            className="w-full"
          />
        </label>
        {app?.volumes?.length ? <p className="text-sm text-neutral-500">{t('readyAppsPersistentHint')}</p> : null}
        {checking ? <p className="text-sm text-neutral-500">{t('readyAppsDomainChecking')}</p> : null}
        {available === true ? <p className="text-sm text-emerald-600">{t('readyAppsDomainAvailable')}</p> : null}
        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={creating}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={creating || available !== true || !title.trim()}>
            {creating ? <LoaderCircle size={16} className="animate-spin" /> : null}
            {creating ? t('readyAppsDeploying') : t('readyAppsDeploy')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
